/**
 * Utility functions for lead panel and reasoning display
 */

/**
 * Normalize source hostname to friendly display name
 */
export const normalizeSourceName = (hostname: string): string => {
  const domain = hostname.replace(/^www\./i, '').toLowerCase();
  const sourceNameMap: { [key: string]: string } = {
    'techcrunch.com': 'TechCrunch',
    'fieldai.com': 'FieldAI',
    'constructiondive.com': 'Construction Dive',
    'cnbc.com': 'CNBC',
    'crunchbase.com': 'Crunchbase',
    'prnewswire.com': 'PR Newswire',
    'bloomberg.com': 'Bloomberg',
    'forbes.com': 'Forbes',
    'reuters.com': 'Reuters',
    'wsj.com': 'The Wall Street Journal',
    'nytimes.com': 'The New York Times',
    'theverge.com': 'The Verge',
    'wired.com': 'WIRED',
    'bbc.com': 'BBC',
    'cnn.com': 'CNN',
    'linkedin.com': 'LinkedIn',
    'pitchbook.com': 'PitchBook'
  };
  return sourceNameMap[domain] || domain.split('.').slice(0, -1).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
};

/**
 * Parse source URL from various formats (string, object, JSON string)
 */
export interface ParsedSource {
  url: string;
  hostname: string;
  title?: string;
  snippet?: string;
  justification?: string;
}

export const parseSourceUrl = (source: any): ParsedSource | null => {
  try {
    let url = '';
    let title = '';
    let snippet = '';

    if (typeof source === 'string' && source.startsWith('http')) {
      url = source;
    } else if (typeof source === 'object' && source.url) {
      url = source.url;
      title = source.title || '';
      snippet = source.snippet || '';
    } else if (typeof source === 'string' && source.startsWith('{')) {
      const parsed = JSON.parse(source);
      url = parsed.url;
      title = parsed.title || '';
      snippet = parsed.snippet || '';
    }

    if (!url) return null;

    const hostname = new URL(url).hostname;
    return { url, hostname, title, snippet: snippet || undefined };
  } catch (e) {
    console.error('Failed to parse source URL:', e);
    return null;
  }
};

/**
 * Get favicon URL for a domain
 */
export const getFaviconUrl = (hostname: string, size: number = 32): string => {
  return `https://www.google.com/s2/favicons?sz=${size}&domain=${hostname}`;
};

/**
 * Extracts hostname from a URL string
 * Handles URLs with or without protocol prefix
 */
export const getHostname = (url: string): string => {
  if (!url || typeof url !== 'string') return '';
  try {
    const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
    return new URL(urlWithProtocol).hostname.replace('www.', '');
  } catch {
    // Fallback for invalid URLs
    return url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0] || '';
  }
};

/**
 * Transform description: split on semicolons and bullets with proper sentence-casing
 */
export const formatDescription = (desc: string): string[] => {
  if (!desc) return [];

  // Split on semicolons or bullets, then clean up
  const parts = desc.split(/[;•]/).map(part => part.trim()).filter(part => part.length > 0);

  // Sentence-case each item
  return parts.map((item) => {
    // Remove trailing periods
    let cleaned = item.replace(/\.$/, '');

    // Capitalize first letter of first word (preserve rest of string for proper nouns)
    if (cleaned.length > 0) {
      cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }

    return cleaned;
  });
};

/**
 * Format date string to readable format
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const isCsvUpload = (source: string): boolean => {
  return source === 'csv_upload';
}